import {
	PiEnvelopeSimple, PiEnvelopeSimpleOpen, PiLinkSimple, PiWarning, PiFlagPennant,
	PiPhoneOutgoing, PiPhoneIncoming, PiPaperclip, PiKey, PiChatCircleText, PiQrCode,
} from "react-icons/pi";

/** Icon + color per `status[].message`, same color families as the activity-status badges elsewhere in the app. */
const OVERVIEW_ICON_META = {
	'Email Sent': { Icon: PiEnvelopeSimple, colorClass: 'is-primary' },
	'SMS Sent': { Icon: PiEnvelopeSimple, colorClass: 'is-primary' },
	'Message Sent': { Icon: PiEnvelopeSimple, colorClass: 'is-primary' },
	'Call Initiated': { Icon: PiPhoneOutgoing, colorClass: 'is-primary' },
	'Email Opened': { Icon: PiEnvelopeSimpleOpen, colorClass: 'is-success' },
	'Call Picked': { Icon: PiPhoneIncoming, colorClass: 'is-success' },
	'Call Received': { Icon: PiPhoneIncoming, colorClass: 'is-success' },
	'Link Clicked': { Icon: PiLinkSimple, colorClass: 'is-warning' },
	'Attachment Opened': { Icon: PiPaperclip, colorClass: 'is-warning' },
	'Data Submitted': { Icon: PiKey, colorClass: 'is-danger' },
	'System Hacks': { Icon: PiWarning, colorClass: 'is-danger' },
	'System Hacked': { Icon: PiWarning, colorClass: 'is-danger' },	/* legacy spelling, kept so older callers still resolve */
	'Error': { Icon: PiWarning, colorClass: 'is-danger' },
	'Replied': { Icon: PiChatCircleText, colorClass: 'is-success' },
	'Email Reported': { Icon: PiFlagPennant, colorClass: 'is-success' },
};

/** Which `status[].message` keys are relevant per attack vector (Vishing/WhatsApp vary by inbound vs outbound), same rule as CampaignEdit's getActivityFilterOptions. */
const VISHING_OUTBOUND = ['Call Initiated', 'Call Picked', 'Data Submitted'];
const VISHING_INBOUND = ['Email Sent', 'Email Opened', 'Call Received', 'Data Submitted', 'Replied', 'Email Reported'];
const WHATSAPP_OUTBOUND = ['Message Sent', 'Link Clicked', 'Data Submitted'];
const WHATSAPP_INBOUND = ['Email Sent', 'Email Opened', 'Link Clicked', 'Data Submitted', 'Replied', 'Email Reported'];
const ATTACK_VECTOR_STATUS_ORDER = {
	Phishing: ['Email Sent', 'Email Opened', 'Link Clicked', 'Data Submitted', 'Email Reported'],
	Ransomware: ['Email Sent', 'Email Opened', 'Link Clicked', 'System Hacks', 'Email Reported'],
	Smishing: ['SMS Sent', 'Link Clicked', 'Data Submitted'],
	Attachment: ['Email Sent', 'Email Opened', 'Attachment Opened', 'Email Reported'],
	QRcode: ['Email Sent', 'Email Opened', 'Link Clicked', 'Data Submitted', 'Email Reported'],
	Telegram: WHATSAPP_OUTBOUND,
};

/** Sums a `status[].count` (by message) across all of a campaign's simulations, including follow-ups. */
export const getStatusCount = (simulations, message) => {
	return (simulations || []).reduce((sum, sim) => {
		const entry = (sim.status || []).find((st) => st.message === message);
		return sum + (entry && typeof entry.count === 'number' ? entry.count : 0);
	}, 0);
};

/** Normalizes a credential-harvesting flag from the API (boolean, 0/1, or on/off-style strings). */
const coerceCredentialHarvesting = (raw) => {
	if (raw === true || raw === false) return raw;
	if (raw === 1 || raw === 0) return raw === 1;
	if (typeof raw === 'string') {
		const s = raw.trim().toLowerCase();
		if (s === 'true' || s === '1' || s === 'yes' || s === 'on') return true;
		if (s === 'false' || s === '0' || s === 'no' || s === 'off' || s === '') return false;
	}
	return false;
};

/** Which status messages to show in the "Primary Campaign Overview" icon row, based on attack vector (and inbound/outbound for Vishing/WhatsApp). */
export const getOverviewStatusMessages = (campaign) => {
	const attackType = campaign?.attack_type?.name;
	const targetInteractionType = campaign?.assign_templates?.[0]?.campaign_template?.targetInteractionType;
	let list;
	if (attackType === 'Vishing') {
		list = targetInteractionType === 'inbound' ? VISHING_INBOUND : VISHING_OUTBOUND;
	} else if (attackType === 'WhatsApp') {
		list = targetInteractionType === 'inbound' ? WHATSAPP_INBOUND : WHATSAPP_OUTBOUND;
	} else {
		list = ATTACK_VECTOR_STATUS_ORDER[attackType];
	}
	list = Array.isArray(list) ? list.slice(0, 5) : [];

	// "Data Submitted" only applies when credential harvesting is on, except for Vishing which only ever offers that option.
	if (attackType !== 'Vishing') {
		const primarySimulation = campaign?.simulations?.[0];
		const credentialHarvestingRaw = primarySimulation?.credential_harvesting ?? primarySimulation?.credentialHarvesting;
		const credentialHarvesting = coerceCredentialHarvesting(credentialHarvestingRaw);
		if (!credentialHarvesting) {
			list = list.filter((message) => message !== 'Data Submitted');
		}
	}

	return list;
};

/** Icon/color/label for one overview status message; QRcode reuses the "Link Clicked" status but should read/show as "QR Scanned". */
export const getOverviewIconMeta = (message, attackType) => {
	if (attackType === 'QRcode' && message === 'Link Clicked') {
		return { Icon: PiQrCode, colorClass: 'is-warning', label: 'QR Scanned' };
	}
	const meta = OVERVIEW_ICON_META[message] || { Icon: PiFlagPennant, colorClass: 'is-primary' };
	return { ...meta, label: message };
};

/**
 * Which per-template stat fields (flat camelCase counts from `/api/campaigns/:id/templates`,
 * e.g. `emailSent`/`linkClicked`) to show for the "By Templates" card, per attack vector.
 * `message` is the canonical status-message string used to look up icon/color/label via `getOverviewIconMeta`.
 */
export const getByTemplateStatFields = (attackType, targetInteractionType, isAIbased) => {
	const inbound = targetInteractionType === 'inbound';
	/* WhatsApp inbound only uses the email funnel when the template is AI-based —
	   same rule the backend dispatch uses (campaign.controller getTemplateAnalytics). */
	const whatsappInbound = inbound && !!isAIbased;
	const emailFunnel = [
		{ field: 'emailSent', message: 'Email Sent' },
		{ field: 'emailOpened', message: 'Email Opened' },
		{ field: 'linkClicked', message: 'Link Clicked' },
		{ field: 'dataSubmitted', message: 'Data Submitted' },
	];
	const messageFunnel = [
		{ field: 'messageSent', message: 'Message Sent' },
		{ field: 'linkClicked', message: 'Link Clicked' },
		{ field: 'dataSubmitted', message: 'Data Submitted' },
	];
	const byAttack = {
		Phishing: emailFunnel,
		QRcode: emailFunnel,
		Attachment: [
			{ field: 'emailSent', message: 'Email Sent' },
			{ field: 'emailOpened', message: 'Email Opened' },
			{ field: 'attachmentOpened', message: 'Attachment Opened' },
		],
		Ransomware: [
			{ field: 'emailSent', message: 'Email Sent' },
			{ field: 'emailOpened', message: 'Email Opened' },
			{ field: 'linkClicked', message: 'Link Clicked' },
			{ field: 'systemHacks', message: 'System Hacks' },
		],
		Vishing: inbound
			? [
				{ field: 'emailSent', message: 'Email Sent' },
				{ field: 'emailOpened', message: 'Email Opened' },
				{ field: 'callReceived', message: 'Call Received' },
				{ field: 'dataSubmitted', message: 'Data Submitted' },
			]
			: [
				{ field: 'callInitiated', message: 'Call Initiated' },
				{ field: 'callPicked', message: 'Call Picked' },
				{ field: 'dataSubmitted', message: 'Data Submitted' },
			],
		WhatsApp: whatsappInbound ? emailFunnel : messageFunnel,
		Telegram: messageFunnel,
		Smishing: [
			{ field: 'smsSent', message: 'SMS Sent' },
			{ field: 'linkClicked', message: 'Link Clicked' },
			{ field: 'dataSubmitted', message: 'Data Submitted' },
		],
		'Removal Media': [],
	};
	return byAttack[attackType] || byAttack.Phishing;
};
